import { MetadataEntityFieldEditorType, MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import { MetadataEntityRecordScope } from '../../metadata/entity/decorators/model/metadata.entity.record.scope';

export type BulkActionKind = 'field-update' | 'command';

/**
 * Operation applied by a field-update bulk action. Defaults to 'set' (replace value).
 * 'add' / 'remove' are used for array-valued fields like Tag where the action
 * should append or subtract rather than replace.
 */
export type BulkActionOperation = 'set' | 'add' | 'remove';

export interface BulkActionEditorFieldCondition {
    field: string;
    optionDataPath: string;
    equals: string | number | boolean;
}

export interface BulkActionEditorField {
    id: string;
    type: MetadataEntityFieldType | [MetadataEntityFieldType];
    typeData?: any;
    editor?: MetadataEntityFieldEditorType;
    required?: boolean;
    displayName?: string;
    requiredWhen?: BulkActionEditorFieldCondition;
}

export interface BulkActionEditor {
    fields: BulkActionEditorField[];
}

export interface BulkActionConfirmation {
    title: string;
    body: string;
}

export interface BulkActionSelection {
    min: number;
    max?: number;
}

export function isBulkActionSelectionAllowed(selection: BulkActionSelection | undefined, count: number): boolean {

    if (!selection) return true;
    return count >= selection.min && (selection.max === undefined || count <= selection.max);
}

export interface BulkActionDescriptor {
    id: string;
    label: string;
    icon?: string;
    kind: BulkActionKind;
    /** Enum grain or absolute embedded path for n-level secondary chains. */
    scope: MetadataEntityRecordScope | string;
    field?: string;
    /** For field-update kinds. Defaults to 'set'; Tag fields produce two descriptors with 'add' / 'remove'. */
    operation?: BulkActionOperation;
    editor?: BulkActionEditor;
    confirmation?: BulkActionConfirmation;
    selection?: BulkActionSelection;

    /**
     * Surfaces this action as a button on the selection bar instead of only inside the Actions palette.
     * Opt-in per entity: when no descriptor declares a boolean policy, every palette-less command stays
     * on the bar as before. An explicit false-only policy leaves just Actions and the selection control.
     * The palette always lists every command, including custom commands without descriptors.
     */
    primary?: boolean;

    /**
     * Accepted by the bulk endpoint but never listed in the Actions palette or on the selection bar: another
     * action's dialog sends it.
     */
    hidden?: boolean;

    /** Required security rights to invoke this action. Values are numeric enum members from the caller's security module (e.g. JobSecurityRight.BulkUpdate). */
    requiredRights?: number[];
}

export type BulkActionDescriptorInput = Omit<BulkActionDescriptor, 'kind' | 'scope'> & {
    kind?: BulkActionKind;
    /** Optional target record grain. Accepts a canonical scope or an absolute embedded path. */
    scope?: MetadataEntityRecordScope | string;
};
